import { type JSX, type ReactPortal, useContext } from 'react';
import { createPortal } from 'react-dom';

import type { Event } from '../../types';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { dialogActiveAtom, shouldShowPopoverDialogAtom } from '../../atoms';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import dayjs from 'dayjs';
import { PopoverWarningDialog } from '../../components';
import { Box } from '@mui/material';

type DragPortalPragmaticProps = {
  activeEvent: Event | null;
  selectedIds: string[];
  pendingDropPosition: { x: number; y: number } | null;
};

/**
 * Simplified drag portal for Pragmatic DnD — no custom DragOverlay since the
 * native browser ghost handles the visual during drag. This only handles the
 * recurring-appointment confirmation popover that appears at the drop position.
 */
const DragPortalPragmatic = ({
  activeEvent,
  selectedIds,
  pendingDropPosition
}: DragPortalPragmaticProps): ReactPortal | JSX.Element | null => {
  'use memo';
  // Hooks must run unconditionally on every render, so they stay above the
  // early return below (also lets the React Compiler optimize this component).
  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      scheduleStart: popoverScheduleStart
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const { isLoading, onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const setDialogActive = useSetRecoilState(dialogActiveAtom);

  if (!activeEvent || !pendingDropPosition) return null;

  const hasStart = !!(popoverScheduleStart && activeEvent?.start);
  const popoverScheduleStartDate: string = dayjs(popoverScheduleStart).isValid()
    ? dayjs(popoverScheduleStart).format('YYYY-MM-DD')
    : '';
  const currentScheduleStartDate: string = dayjs(activeEvent?.start).isValid()
    ? dayjs(activeEvent?.start).format('YYYY-MM-DD')
    : '';

  const isPopOverId = popoverOccurenceId === activeEvent?.id;

  const showPopoverDialog: boolean =
    activeEvent?.isRecurring &&
    (isPopOverId ||
      popoverOccurenceId === 'IS_MULTIPLE' ||
      (isPopOverId &&
        hasStart &&
        popoverScheduleStartDate === currentScheduleStartDate));

  return createPortal(
    <PopoverWarningDialog
      open={showPopoverDialog && !isLoading}
      isMultipleSelected={selectedIds.length > 1}
      onClose={() => {
        onClose?.();
        setShouldShowPopoverDialog({ id: '' });
        setDialogActive(false);
      }}
      onClickAppointment={(editAppointmentType: EditAppointmentType) => {
        onHandleAppointmentConfirmation?.(editAppointmentType, isUnassignment);
        setShouldShowPopoverDialog({ id: '' });
      }}
    >
      {/* Minimal anchor element positioned at the drop location */}
      <Box
        sx={{
          position: 'fixed',
          top: pendingDropPosition.y,
          left: pendingDropPosition.x,
          width: 0,
          height: 0,
          zIndex: 99
        }}
      />
    </PopoverWarningDialog>,
    document.body
  );
};

export default DragPortalPragmatic;
